import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';

interface User {
  id: string;
  username: string;
  role: string;
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Инициализация Axios с токеном
  useEffect(() => {
    authService.setupAxios();
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      console.log('Stored token found:', !!storedToken);
      
      if (storedToken) {
        try {
          const decodedToken = jwtDecode<any>(storedToken);
          console.log('Decoded token:', decodedToken);
          
          // Check token expiration
          const currentTime = Date.now() / 1000;
          if (decodedToken.exp && decodedToken.exp < currentTime) {
            console.log('Token expired');
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
            navigate('/login');
            enqueueSnackbar('Сессия истекла. Пожалуйста, войдите снова.', { 
              variant: 'warning' 
            });
            return;
          }

          setUser({
            id: decodedToken.sub?.toString() || '0',
            username: decodedToken.username || 'admin',
            role: (decodedToken.roles && decodedToken.roles[0]) || 'admin',
            permissions: decodedToken['https://hasura.io/jwt/claims']?.['x-hasura-allowed-roles'] || ['admin']
          });
          
          setToken(storedToken);
        } catch (error) {
          console.error('Ошибка при декодировании токена:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
          navigate('/login');
          enqueueSnackbar('Сессия истекла. Пожалуйста, войдите снова.', { 
            variant: 'warning' 
          });
        }
      } else {
        console.log('No token found, user needs to log in');
      }
      setIsLoading(false);
    };

    initAuth();
  }, [navigate, enqueueSnackbar]);

  const login = async (username: string, password: string) => {
    try {
      // Use authService instead of api directly
      const authResult = await authService.login(username, password);
      const { accessToken } = authResult;
      
      // Store the JWT token
      localStorage.setItem('token', accessToken);
      // Remove old hasura secret if exists
      localStorage.removeItem('hasura_admin_secret');

      // Set token in state
      setToken(accessToken);

      // Decode and set user info
      try {
        const decodedToken = jwtDecode<any>(accessToken);
        console.log('Decoded token:', decodedToken);
        
        setUser({
          id: decodedToken.sub?.toString() || '0',
          username: decodedToken.username || username,
          role: (decodedToken.roles && decodedToken.roles[0]) || 'admin',
          permissions: decodedToken['https://hasura.io/jwt/claims']?.['x-hasura-allowed-roles'] || ['admin']
        });

        // Navigate to dashboard
        navigate('/dashboard');
        enqueueSnackbar(`Добро пожаловать, ${decodedToken.username || username}!`, { 
          variant: 'success' 
        });
      } catch (validationError) {
        console.error('Ошибка при декодировании токена:', validationError);
        throw new Error('Некорректный формат токена от сервера');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || 'Failed to login');
    }
  };

  const logout = () => {
    console.log('Logging out user');
    authService.logout();
    setToken(null);
    setUser(null);
    navigate('/login');
    enqueueSnackbar('Вы успешно вышли из системы', { 
      variant: 'success' 
    });
  };

  const hasPermission = (permission: string) => {
    if (!user) return false;
    
    // Для Hasura проверяем роль пользователя
    if (user.role === 'admin') return true;
    
    // Проверяем разрешения
    return user.permissions.includes(permission);
  };

  const value = {
    user,
    token,
    isAuthenticated: !!token,
    isLoading,
    login,
    logout,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 